/**
 * AgeInput Component
 *
 * Reusable input component for age values with minimum validation
 *
 * @component
 * @example
 * ```tsx
 * <AgeInput
 *   name="currentAge"
 *   value={35}
 *   onChange={(field, value) => handleChange(field, value)}
 *   onBlur={(field, value) => handleBlur(field, value)}
 *   label="Current Age"
 *   min={18}
 *   error={errors.currentAge}
 * />
 * ```
 */

import React from "react";
import { cn } from "../utils/cn";
import {
  createNumericChangeHandler,
  createNumericBlurHandler,
} from "../utils/numericInput";
import { renderHelpText } from "../utils/helpTextRenderer";

export interface AgeInputProps {
  /** Field name for form handling */
  name: string;
  /** Current numeric value */
  value?: number | null;
  /** Callback when value changes (optimistic update) */
  onChange: (name: string, value: number | null) => void;
  /** Callback when input loses focus (triggers save) */
  onBlur: (name: string, value: number | null) => void;
  /** Label text displayed above input */
  label: string;
  /** Minimum allowed age (typically 18) */
  min: number;
  /** Maximum allowed age (optional, no upper limit if not provided) */
  max?: number;
  /** Optional error message */
  error?: string;
  /** Optional help text displayed below the field */
  helpText?: string;
  /** Optional suffix to display on the right side of input (e.g., "yrs") */
  suffix?: string;
  /** Optional placeholder text */
  placeholder?: string;
}

/**
 * AgeInput - Input field for age values
 *
 * Features:
 * - Validates minimum age (18 by default)
 * - No maximum age limit (simplified constraints)
 * - Integer values only (step 1)
 * - Converts empty input to 0
 * - Displays error messages
 * - Accessible with proper labels
 * - Shows "years" indicator in label
 *
 * Common use cases:
 * - Current age
 * - Retirement age
 * - Life expectancy
 *
 * @param props - Component props
 * @returns Age input field
 */
const AgeInput: React.FC<AgeInputProps> = ({
  name,
  value,
  onChange,
  onBlur,
  label,
  min,
  max,
  error,
  helpText,
  suffix,
  placeholder,
}) => {
  const handleChange = createNumericChangeHandler(name, onChange);
  const handleBlur = createNumericBlurHandler(name, onBlur);

  return (
    <div>
      <label htmlFor={name} className="form-label">
        {label}
      </label>
      <div className="form-input-wrapper">
        <input
          type="number"
          id={name}
          name={name}
          value={value === null || value === undefined ? "" : value}
          onChange={handleChange}
          onBlur={handleBlur}
          onWheel={(e) => e.currentTarget.blur()}
          min={min}
          max={max}
          step="1"
          className={cn("form-input", suffix && "form-input-with-suffix")}
          placeholder={placeholder ?? `e.g., ${min + 10}`}
        />
        {suffix && <span className="age-input-suffix">{suffix}</span>}
      </div>
      {helpText && <p className="form-help-text">{renderHelpText(helpText)}</p>}
      {error && <p className="form-error">{error}</p>}
    </div>
  );
};

export default AgeInput;
